import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Canceler } from "axios";
import { useAppTable, useFormContainerState, useTrans } from "../../../hooks";
import {
    VStack,
    HStack,
    useToast,
    Checkbox,
    Box,
    Button,
    Text,
    useDisclosure,
} from "@chakra-ui/react";
import { updatePageIndex } from "ka-table/actionCreators";
import { ICellTextProps } from "ka-table/props";
import { AppSearch } from "../../../containers";
import {
    AppAlertMessage,
    AppFilterButton,
    AppIcon,
    AppLoader,
    AppPageContent,
    AppPageHeader,
} from "../../../components";
import { StudentList as ListComponent } from "./StudentList";
import { StudentFilter as FilterComponent } from "./StudentFilter";
import {
    Internship,
    InternshipSchedule,
    InternshipScheduleSeat,
    InternshipScheduleSeatApi,
    User as Entity,
    UserApi as EntityApi,
    InternshipStudentApi,
    InternshipStudent,
    TrainingApi,
    Training,
    TrainingYear,
    TrainingYearApi,
} from "../../../apis";
import { APPC, cPath } from "../../../config";
import { useAuthData } from "../../../contexts";

export const StudentPage = () => {
    // hook
    const { t } = useTrans();
    const toast = useToast();
    const { internshipScheduleSeatId } = useParams();
    const formContainerState = useFormContainerState();
    const {
        isOpen: isOpenMessage,
        onOpen: onOpenMessage,
        onClose: onCloseMessage,
    } = useDisclosure();
    const { grant, user } = useAuthData();

    // state & const
    const [search, setSearch] = useState("");
    const [filterData, setFilterData] = useState<any>({});
    const [loading, setLoading] = useState(true);
    const [loadingList, setLoadingList] = useState(true);
    const [loadingList2, setLoadingList2] = useState(true);
    const [internshipScheduleSeat, setInternshipScheduleSeat] =
        useState<InternshipScheduleSeat>();
    const [list, setList] = useState<Entity[]>([]);
    const [list2, setList2] = useState<Entity[]>([]);

    const cancelTokenSourcesRef = useRef<Canceler[]>([]);

    const {
        table,
        pageIndex,
        pagesCount,
        pageSize,
        sortColumn,
        sortOrder,
        setPagesCount,
        getSelectRow,
        setSelectRow,
        getSelectAll,
        setSelectAll,
        getSelectIds,
        getSelectTotal,
        clearSelect,
    } = useAppTable("firstName");

    const {
        table: table2,
        pageIndex: pageIndex2,
        pagesCount: pagesCount2,
        pageSize: pageSize2,
        sortColumn: sortColumn2,
        sortOrder: sortOrder2,
        setPagesCount: setPagesCount2,
        getSelectRow: getSelectRow2,
        setSelectRow: setSelectRow2,
        getSelectAll: getSelectAll2,
        setSelectAll: setSelectAll2,
        getSelectIds: getSelectIds2,
        getSelectTotal: getSelectTotal2,
        clearSelect: clearSelect2,
    } = useAppTable("firstName");

    const paging = {
        enabled: true,
        pageSizes: APPC.PAGE_SIZES,
        pageSize,
        pageIndex,
        pagesCount,
    };

    const paging2 = {
        enabled: true,
        pageSizes: APPC.PAGE_SIZES,
        pageSize: pageSize2,
        pageIndex: pageIndex2,
        pagesCount: pagesCount2,
    };

    // handler
    const fetchListData = async () => {
        setLoadingList(true);
        const params = {
            itemsPerPage: pageSize,
            internshipScheduleSeatId,
            ...filterData,
            "training.id": TrainingApi.toResourceIRI(
                (
                    (
                        (
                            internshipScheduleSeat?.internshipSchedule as InternshipSchedule
                        ).internship as Internship
                    ).training as Training
                ).id
            ),
            "trainingYear.id": TrainingYearApi.toResourceIRI(
                (
                    (
                        internshipScheduleSeat?.internshipSchedule as InternshipSchedule
                    ).trainingYear as TrainingYear
                )?.id
            ),
        };
        params[`order[${sortColumn}]`] = sortOrder;
        if (search) {
            params["user_search"] = search;
        }

        if (grant.isPedagogicalReferenceTrainer) {
            params["studentTrainer.id"] = user?.id;
        }

        await EntityApi.getCollectionForAssign<Entity>(
            pageIndex + 1,
            params,
            (c) => {
                cancelTokenSourcesRef.current.push(c);
            }
        )
            .then(({ errorMessage, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    setPagesCount(Math.ceil(response.totalItems / pageSize));
                    setList(response.items);
                }
            })
            .finally(() => setLoadingList(false));
    };

    const fetchListData2 = async () => {
        setLoadingList2(true);
        const params = {
            itemsPerPage: pageSize2,
            internshipScheduleSeatId: internshipScheduleSeatId || 0,
            // ...filterData,
        };
        params[`order[${sortColumn2}]`] = sortOrder2;
        // if (search) {
        //     params["user_search"] = search;
        // }

        if (grant.isPedagogicalReferenceTrainer) {
            params["studentTrainer.id"] = user?.id;
        }

        await EntityApi.getCollectionAssign<Entity>(
            pageIndex2 + 1,
            params,
            (c) => {
                cancelTokenSourcesRef.current.push(c);
            }
        )
            .then(({ errorMessage, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    setPagesCount2(Math.ceil(response.totalItems / pageSize2));
                    setList2(response.items);
                }
            })
            .finally(() => setLoadingList2(false));
    };

    const fetchInternshipScheduleSeat = () => {
        setLoading(true);
        if (internshipScheduleSeatId) {
            InternshipScheduleSeatApi.getItemMyInternship<InternshipScheduleSeat>(
                internshipScheduleSeatId
            )
                .then(({ errorMessage, isNotFound, response }) => {
                    if (errorMessage) {
                        toast({
                            title: errorMessage,
                            status: "error",
                        });
                    } else if (isNotFound) {
                        toast({
                            title: t("cmn:message.entityNotFound"),
                            status: "error",
                        });
                    } else if (response !== null) {
                        setInternshipScheduleSeat(response);
                    }
                })
                .finally(() => setLoading(false));
        }
    };

    const assignStudentHandler = (isRemove: boolean) => {
        if (!isRemove) {
            const totalSelectedIds = getSelectTotal();
            if (
                totalSelectedIds >
                (internshipScheduleSeat?.noOfSeats ?? 0) -
                    (internshipScheduleSeat?.noOfAssignedSeats ?? 0)
            ) {
                onOpenMessage();
                return;
            }
        }

        const studentIds = isRemove ? getSelectIds2() : getSelectIds();
        if (!internshipScheduleSeat) {
            return;
        }

        const internshipStudent = {
            internshipScheduleSeat: InternshipScheduleSeatApi.toResourceIRI(
                internshipScheduleSeat.id
            ),
            students: studentIds,
            isRemove: isRemove,
        } as InternshipStudent;

        setLoading(true);
        InternshipStudentApi.createOrUpdate<
            { success: boolean },
            InternshipStudent
        >(0, internshipStudent)
            .then(({ response, errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    clearSelect();
                    clearSelect2();
                    table.dispatch(updatePageIndex(0));
                    table2.dispatch(updatePageIndex(0));
                    fetchInternshipScheduleSeat();
                    fetchListData();
                    fetchListData2();
                    toast({
                        title: t("cmn:message.save.success"),
                        status: "success",
                    });
                }
            })
            .finally(() => setLoading(false));
    };

    const SelectionCell = ({ rowKeyValue }: ICellTextProps) => {
        if (grant.isInternshipCoordinatorTrainer || grant.isSchoolAndCompany) {
            return (
                <Checkbox
                    isChecked={getSelectRow(pageIndex, rowKeyValue)}
                    onChange={(e) =>
                        setSelectRow(pageIndex, rowKeyValue, e.target.checked)
                    }
                />
            );
        }
        return <></>;
    };

    const SelectionHeader = () => {
        if (grant.isInternshipCoordinatorTrainer || grant.isSchoolAndCompany) {
            return (
                <Checkbox
                    isChecked={getSelectAll(pageIndex) === "ALL"}
                    isIndeterminate={getSelectAll(pageIndex) === "SOME"}
                    onChange={() => setSelectAll(pageIndex)}
                />
            );
        }
        return <></>;
    };

    const SelectionCell2 = ({ rowKeyValue }: ICellTextProps) => {
        if (grant.isInternshipCoordinatorTrainer || grant.isSchoolAndCompany) {
            return (
                <Checkbox
                    isChecked={getSelectRow2(pageIndex2, rowKeyValue)}
                    onChange={(e) =>
                        setSelectRow2(pageIndex2, rowKeyValue, e.target.checked)
                    }
                />
            );
        }
        return <></>;
    };

    const SelectionHeader2 = () => {
        if (grant.isInternshipCoordinatorTrainer || grant.isSchoolAndCompany) {
            return (
                <Checkbox
                    isChecked={getSelectAll2(pageIndex2) === "ALL"}
                    isIndeterminate={getSelectAll2(pageIndex2) === "SOME"}
                    onChange={() => setSelectAll2(pageIndex2)}
                />
            );
        }
        return <></>;
    };

    // useEffect
    useEffect(() => {
        if (!formContainerState.isOpen && internshipScheduleSeat) {
            fetchListData();
        }
    }, [
        pageIndex,
        pageSize,
        sortColumn,
        sortOrder,
        search,
        filterData,
        formContainerState.isOpen,
        internshipScheduleSeat,
    ]);

    useEffect(() => {
        if (!formContainerState.isOpen && internshipScheduleSeat) {
            fetchListData2();
        }
    }, [
        pageIndex2,
        pageSize2,
        sortColumn2,
        sortOrder2,
        search,
        filterData,
        formContainerState.isOpen,
        internshipScheduleSeat,
    ]);

    useEffect(() => {
        fetchInternshipScheduleSeat();
    }, [internshipScheduleSeatId]);

    useEffect(() => {
        if (pageIndex > 0 || pageIndex2 > 0) {
            table.dispatch(updatePageIndex(0));
            table2.dispatch(updatePageIndex(0));
        }
    }, [filterData]);

    if (loading) {
        return <AppLoader />;
    }

    return (
        <>
            <AppPageHeader
                title={t("padm.StudentPage:text.pageTitle")}
                breadcrumb={
                    internshipScheduleSeat
                        ? [
                              {
                                  label: `${
                                      (
                                          (
                                              internshipScheduleSeat?.internshipSchedule as InternshipSchedule
                                          )?.internship as Internship
                                      ).name
                                  } (${
                                      (
                                          internshipScheduleSeat?.internshipSchedule as InternshipSchedule
                                      ).name
                                  })`,
                                  url: cPath("ADMIN.MY_INTERNSHIP_PAGE"),
                              },
                          ]
                        : []
                }
            >
                <HStack gap={4} py={{ base: 3.5, sm: 4, md: "1.125rem" }}>
                    <AppSearch
                        onSearch={(value) => {
                            setSearch(value);
                            if (pageIndex > 0 || pageIndex2 > 0) {
                                table.dispatch(updatePageIndex(0));
                                table2.dispatch(updatePageIndex(0));
                            }
                        }}
                    />
                    <FilterComponent
                        filterData={filterData}
                        setFilterData={setFilterData}
                    >
                        <Box role="button">
                            <AppFilterButton filterData={filterData} />
                        </Box>
                    </FilterComponent>
                </HStack>
            </AppPageHeader>
            <AppPageContent>
                <VStack p={6} alignItems={"flex-start"} gap={4}>
                    <Box
                        display={"flex"}
                        w={"full"}
                        gap={"4"}
                        className="ka-paging"
                        alignItems={"center"}
                        justifyContent={"space-between"}
                    >
                        <HStack gap={4}>
                            {(grant.isInternshipCoordinatorTrainer ||
                                grant.isSchoolAndCompany) && (
                                <Button
                                    onClick={() => assignStudentHandler(false)}
                                    disabled={
                                        getSelectTotal() > 0 ? false : true
                                    }
                                >
                                    <Text mr={2}>
                                        {t("padm.StudentPage:button.assign")}
                                    </Text>
                                    <AppIcon name="ics-arrow-right" w="1rem" />
                                </Button>
                            )}
                            {internshipScheduleSeat && (
                                <Box>
                                    <Text textStyle={"regularLg"}>
                                        {t(
                                            "padm.StudentPage:text.availableSeats"
                                        )}
                                        :{" "}
                                        <strong>
                                            {(internshipScheduleSeat?.noOfSeats ??
                                                0) -
                                                (internshipScheduleSeat?.noOfAssignedSeats ??
                                                    0)}
                                        </strong>
                                    </Text>
                                </Box>
                            )}
                        </HStack>
                        <HStack gap={4}>
                            {internshipScheduleSeat && (
                                <Box>
                                    <Text textStyle={"regularLg"}>
                                        {t(
                                            "ent.InternshipScheduleSeat:noOfAssignedSeats.label"
                                        )}
                                        :{" "}
                                        <strong>
                                            {
                                                internshipScheduleSeat?.noOfAssignedSeats
                                            }
                                        </strong>
                                    </Text>
                                </Box>
                            )}
                            {(grant.isInternshipCoordinatorTrainer ||
                                grant.isSchoolAndCompany) && (
                                <Button
                                    onClick={() => assignStudentHandler(true)}
                                    disabled={
                                        getSelectTotal2() > 0 ? false : true
                                    }
                                >
                                    <AppIcon name="ics-arrow-left" w="1rem" />
                                    <Text ml={2}>
                                        {t("padm.StudentPage:button.unassign")}
                                    </Text>
                                </Button>
                            )}
                        </HStack>
                    </Box>
                    <HStack w={"full"} gap={4} alignItems={"flex-start"}>
                        <ListComponent
                            table={table}
                            paging={paging}
                            list={list}
                            loadingList={loadingList}
                            SelectionCell={SelectionCell}
                            SelectionHeader={SelectionHeader}
                        />
                        <ListComponent
                            table={table2}
                            paging={paging2}
                            list={list2}
                            loadingList={loadingList2}
                            SelectionCell={SelectionCell2}
                            SelectionHeader={SelectionHeader2}
                        />
                    </HStack>
                    {isOpenMessage && (
                        <AppAlertMessage
                            isOpen={isOpenMessage}
                            onCancel={onCloseMessage}
                        >
                            <Text mt={4} textStyle={"mediumMd"}>
                                {t(
                                    "padm.StudentPage:alert.violationOfAvailableSeats.message"
                                )}
                            </Text>
                        </AppAlertMessage>
                    )}
                </VStack>
            </AppPageContent>
        </>
    );
};
